import React, { useState, useEffect, useRef } from 'react';
import { Activity, Wifi, WifiOff, RefreshCw, MapPin, Zap, Thermometer, Droplets, Wind, Gauge, Cloud, Settings } from 'lucide-react';
import Header from '../Components/Header';

const RobustMQTTDataViewer = () => {
  const [deviceData, setDeviceData] = useState({});
  const [connectionStatus, setConnectionStatus] = useState({});
  const [availableDevices, setAvailableDevices] = useState([]);
  const [deviceLocations, setDeviceLocations] = useState({});
  const [lastUpdated, setLastUpdated] = useState({});
  const [errors, setErrors] = useState({});
  
  const wsConnectionsRef = useRef({});

  // Safe parameter processing - handles ANY data type
  const processParameter = (key, value) => {
    try {
      // Skip null/undefined values
      if (value === null || value === undefined) {
        return null;
      }

      // Handle different data types safely
      let displayValue = '';
      let dataType = typeof value;

      switch (dataType) {
        case 'number':
          // Format numbers safely
          if (isNaN(value) || !isFinite(value)) {
            displayValue = 'Invalid';
          } else {
            displayValue = Number.isInteger(value) ? value.toString() : value.toFixed(2);
          }
          break;
          
        case 'boolean':
          displayValue = value ? 'ON' : 'OFF';
          break;
          
        case 'string':
          displayValue = value.toString().substring(0, 50); // Limit string length
          break;
          
        case 'object':
          if (Array.isArray(value)) {
            displayValue = `[${value.length} items]`;
          } else {
            displayValue = 'Object';
          }
          break;
          
        default:
          displayValue = String(value).substring(0, 50);
      }

      return {
        originalKey: key,
        displayName: formatDisplayName(key),
        value: displayValue,
        rawValue: value,
        dataType: dataType,
        unit: guessUnit(key, value),
        icon: getGenericIcon(key)
      };
    } catch (error) {
      console.warn(`Error processing parameter ${key}:`, error);
      return {
        originalKey: key,
        displayName: key,
        value: 'Error',
        rawValue: value,
        dataType: 'error',
        unit: '',
        icon: Activity
      };
    }
  };

  // Safe display name formatting
  const formatDisplayName = (key) => {
    try {
      if (!key || typeof key !== 'string') return 'Unknown';
      
      return key
        .toString()
        .replace(/[_\-\.]/g, ' ')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
        .substring(0, 30); // Limit length
    } catch (error) {
      return key?.toString() || 'Unknown';
    }
  };

  // Generic unit guessing - safe fallback
  const guessUnit = (key, value) => {
    try {
      if (typeof value !== 'number') return '';
      
      const keyLower = key.toString().toLowerCase();
      
      // Common patterns
      if (keyLower.includes('temp')) return '°C';
      if (keyLower.includes('humid') || keyLower.includes('moisture')) return '%';
      if (keyLower.includes('pressure') || keyLower.includes('press')) return 'hPa';
      if (keyLower.includes('wind') && keyLower.includes('speed')) return 'm/s';
      if (keyLower.includes('wind') && keyLower.includes('dir')) return '°';
      if (keyLower.includes('rain') || keyLower.includes('precip')) return 'mm';
      if (keyLower.includes('volt') || keyLower.includes('battery')) return 'V';
      if (keyLower.includes('level') || keyLower.includes('height')) return 'cm';
      if (keyLower.includes('speed') || keyLower.includes('rate')) return 'm/s';
      if (keyLower.includes('percent') || (value >= 0 && value <= 100 && keyLower.includes('hum'))) return '%';
      
      return '';
    } catch (error) {
      return '';
    }
  };

  // Generic icon selection - always returns a valid icon
  const getGenericIcon = (key) => {
    try {
      const keyLower = key.toString().toLowerCase();
      
      if (keyLower.includes('temp')) return Thermometer;
      if (keyLower.includes('humid') || keyLower.includes('water') || keyLower.includes('moisture')) return Droplets;
      if (keyLower.includes('wind') || keyLower.includes('air')) return Wind;
      if (keyLower.includes('pressure')) return Gauge;
      if (keyLower.includes('rain') || keyLower.includes('weather')) return Cloud;
      if (keyLower.includes('batt') || keyLower.includes('volt') || keyLower.includes('power')) return Zap;
      if (keyLower.includes('status') || keyLower.includes('state') || keyLower.includes('config')) return Settings;
      
      return Activity; // Safe fallback
    } catch (error) {
      return Activity;
    }
  };

  // Safe data processing from WebSocket
  const processIncomingData = (deviceId, rawData) => {
    try {
      setErrors(prev => ({ ...prev, [deviceId]: null }));
      
      if (!rawData || typeof rawData !== 'object') {
        throw new Error('Invalid data format');
      }

      const processedParams = {};
      
      // Process each parameter safely
      Object.entries(rawData).forEach(([key, value]) => {
        // Skip system fields
        if (['timestamp', 'type', 'device_id', 'id'].includes(key.toLowerCase())) {
          return;
        }
        
        const processedParam = processParameter(key, value);
        if (processedParam) {
          processedParams[key] = processedParam;
        }
      });

      setDeviceData(prev => ({
        ...prev,
        [deviceId]: processedParams
      }));
      
      setLastUpdated(prev => ({
        ...prev,
        [deviceId]: new Date()
      }));

    } catch (error) {
      console.error(`Error processing data for ${deviceId}:`, error);
      setErrors(prev => ({
        ...prev,
        [deviceId]: error.message
      }));
    }
  };

  // Load device info from localStorage (your existing logic)
  useEffect(() => {
    try {
      const authorizedDevices = JSON.parse(localStorage.getItem('device_ids') || "[]");
      const placeData = JSON.parse(localStorage.getItem('place') || "[]");

      const locationMap = {};
      placeData.forEach(device => {
        if (device.device_id && device.place) {
          locationMap[device.device_id] = device.place;
        }
      });
      
      setDeviceLocations(locationMap);
      setAvailableDevices(authorizedDevices);
    } catch (error) {
      console.error('Error loading device data:', error);
      setAvailableDevices([]);
    }
  }, []);

  // WebSocket connection (your existing logic but with better error handling)
  const connectWebSocket = (deviceId) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setConnectionStatus(prev => ({...prev, [deviceId]: 'Authentication Error'}));
        return null;
      }

      setConnectionStatus(prev => ({...prev, [deviceId]: 'Connecting...'}));
      
      const ws = new WebSocket(`/api/ws/${deviceId}?token=${token}`);
      
      ws.onopen = () => {
        setConnectionStatus(prev => ({...prev, [deviceId]: 'Connected'}));
        setErrors(prev => ({ ...prev, [deviceId]: null }));
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          // Skip heartbeat messages
          if (data.type === 'heartbeat') return;
          
          processIncomingData(deviceId, data);
        } catch (error) {
          console.error(`Error parsing message from ${deviceId}:`, error);
          setErrors(prev => ({
            ...prev,
            [deviceId]: 'Data parsing error'
          }));
        }
      };

      ws.onclose = () => {
        setConnectionStatus(prev => ({...prev, [deviceId]: 'Disconnected'}));
      };
      
      ws.onerror = (error) => {
        console.error(`WebSocket error for ${deviceId}:`, error);
        setConnectionStatus(prev => ({...prev, [deviceId]: 'Connection Error'}));
      };
      
      return ws;
    } catch (error) {
      console.error(`Error creating WebSocket for ${deviceId}:`, error);
      setConnectionStatus(prev => ({...prev, [deviceId]: 'Setup Error'}));
      return null;
    }
  };

  // Connect to all devices (your existing logic)
  useEffect(() => {
    if (availableDevices.length === 0) return;

    availableDevices.forEach(deviceId => {
      const ws = connectWebSocket(deviceId);
      if (ws) {
        wsConnectionsRef.current[deviceId] = ws;
      }
    });

    return () => {
      Object.values(wsConnectionsRef.current).forEach(ws => {
        if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) {
          ws.close();
        }
      });
    };
  }, [availableDevices]);

  // Safe value display
  const renderParameterValue = (param) => {
    try {
      const { value, unit, dataType } = param;
      
      if (dataType === 'error') {
        return <span className="text-red-500 text-sm">Error</span>;
      }
      
      return (
        <span className="font-semibold text-gray-900">
          {value} {unit && <span className="text-gray-600">{unit}</span>}
        </span>
      );
    } catch (error) {
      return <span className="text-red-500 text-sm">Display Error</span>;
    }
  };

  const username = localStorage.getItem("username") || "User";

  return (
    <>
    <Header/>
    
    <div className="min-h-screen bg-gray-50 px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="text-center pt-8 pb-6">
        <h1 className="text-3xl sm:text-4xl font-bold">
          WELCOME BACK <span className="text-blue-500">{username}</span>
        </h1>
        <p className="text-sm text-gray-500 mt-2">
          {Object.values(connectionStatus).filter(status => status === 'Connected').length} of {availableDevices.length} devices connected
        </p>
      </div>

      {/* Device Grid */}
      <div className="max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {availableDevices.length > 0 ? (
          availableDevices.map((deviceId) => {
            const location = deviceLocations[deviceId] || "Unknown Location";
            const data = deviceData[deviceId] || {};
            const status = connectionStatus[deviceId] || 'Connecting...';
            const lastUpdate = lastUpdated[deviceId];
            const error = errors[deviceId];
            
            const statusColorClass = 
              status === 'Connected' ? 'text-green-600' : 
              status === 'Connecting...' ? 'text-yellow-500' : 
              'text-red-500';

            const statusDotClass = 
              status === 'Connected' ? 'bg-green-500' : 
              status === 'Connecting...' ? 'bg-yellow-500' : 
              'bg-red-500';

            return (
              <div
                key={deviceId}
                className="rounded-2xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-200 p-5 bg-white"
              >
                {/* Status indicator */}
                <div className={`absolute top-3 right-3 w-3 h-3 rounded-full ${statusDotClass}`}></div>
                
                {/* Device header */}
                <div className="flex justify-between items-start mb-4">
                  <div className="text-gray-800 font-semibold text-lg">
                    <h2>Device ID</h2>
                    <p className="text-sm">{deviceId}</p>
                  </div>
                  <div className="text-gray-700 text-right text-sm">
                    <h2>Location</h2>
                    <p>{location}</p>
                  </div>
                </div>

                {/* Error display */}
                {error && (
                  <div className="mb-4 p-2 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
                    Error: {error}
                  </div>
                )}

                {/* Parameters */}
                <div className="mt-4 space-y-3">
                  {Object.keys(data).length > 0 ? (
                    Object.entries(data).map(([key, param]) => {
                      const IconComponent = param.icon;
                      
                      return (
                        <div key={`${deviceId}-${key}`} className="flex items-center p-2 hover:bg-gray-50 rounded">
                          <div className="flex-shrink-0 mr-3">
                            <IconComponent className="w-5 h-5 text-blue-500" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-gray-700 truncate">
                              {param.displayName}
                            </div>
                            <div className="text-sm">
                              {renderParameterValue(param)}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="text-gray-500 text-center py-4">
                      <Activity className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                      <p>No data available</p>
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div className="mt-4 pt-3 border-t border-gray-100 flex justify-between items-center text-sm">
                  <div>
                    <span className="text-gray-600">Status: </span>
                    <span className={`font-medium ${statusColorClass}`}>
                      {status}
                    </span>
                  </div>
                  
                  {lastUpdate && (
                    <div className="text-xs text-gray-500">
                      {lastUpdate.toLocaleTimeString()}
                    </div>
                  )}
                </div>

                {/* Reconnect button */}
                {status !== 'Connected' && status !== 'Connecting...' && (
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      
                      if (wsConnectionsRef.current[deviceId]) {
                        try {
                          wsConnectionsRef.current[deviceId].close();
                        } catch (err) {
                          console.error("Error closing connection:", err);
                        }
                      }
                      
                      const newWs = connectWebSocket(deviceId);
                      if (newWs) {
                        wsConnectionsRef.current[deviceId] = newWs;
                      }
                    }}
                    className="mt-2 w-full px-3 py-2 bg-blue-500 text-white text-sm rounded hover:bg-blue-600 transition-colors"
                  >
                    <RefreshCw className="w-4 h-4 inline mr-1" />
                    Reconnect
                  </button>
                )}
              </div>
            );
          })
        ) : (
          <div className="col-span-full text-center py-8">
            <p className="text-gray-500 text-lg">No authorized devices found.</p>
          </div>
        )}
      </div>

      {/* Footer message */}
      <div className="text-center my-6">
        <p className="text-orange-500 font-bold text-xl">Real-time IoT Monitoring</p>
        <p className="text-gray-600">Data refreshes automatically</p>
      </div>
    </div>
    </>
  );
};

export default RobustMQTTDataViewer;
